// src/services/stateSettingsService.js
//
// Runtime source of truth for which states and subjects the portal offers.
//
// WHY THIS EXISTS
// ---------------
// Availability used to be build-time constants in src/config/geography.js, so
// turning a subject on after seeding it meant editing code and redeploying.
// Content sat finished but invisible in the gap. This reads the same decision
// from Firestore instead, so it becomes a switch.
//
// FALLBACK IS DELIBERATE, NOT DEFENSIVE
// -------------------------------------
// If Firestore is unreachable, or stateSettings has not been seeded, every
// helper falls back to the geography.js constants. That means the worst case
// is today's behaviour, not a blank portal. A settings service that fails
// closed would take the whole catalogue offline over a network blip.
//
// CACHING
// -------
// One read per session, held in module scope. These values change a few times
// a month, not per render, and the picker calls them for all 51 states. Call
// refreshStateSettings() after an admin change to pick it up without a reload.

import { collection, doc, getDocs, setDoc } from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";
import {
  isStateAvailable as geoAvailable,
  isScienceReady as geoScienceReady,
  isMathReady as geoMathReady,
} from "../config/geography";

const SUBJECTS = ["science", "math", "socialstudies"];

let cache = null;
let inFlight = null;

/** Loads and caches every state's settings. Safe to call repeatedly. */
export async function loadStateSettings() {
  if (cache) return cache;
  if (inFlight) return inFlight;

  inFlight = (async () => {
    try {
      const snap = await getDocs(collection(db, "stateSettings"));
      if (snap.empty) {
        // Not seeded yet. Null cache means every helper uses geography.js,
        // which is the correct behaviour before the migration runs.
        cache = null;
        return null;
      }
      const map = {};
      snap.forEach((d) => {
        const data = d.data();
        map[String(data.state || d.id).toLowerCase()] = data;
      });
      cache = map;
      return map;
    } catch (err) {
      console.warn("stateSettings unavailable, using geography.js defaults", err);
      cache = null;
      return null;
    } finally {
      inFlight = null;
    }
  })();

  return inFlight;
}

/**
 * Writes one state's availability, then updates the cache in place.
 *
 * Admin-only — firestore.rules restricts stateSettings writes to isAdmin().
 * The local cache is patched rather than dropped so the panel re-renders from
 * the value just saved instead of flickering through a refetch.
 */
export async function saveStateAvailable(state, available) {
  const id = String(state || "").toLowerCase();
  await setDoc(
    doc(db, "stateSettings", id),
    { available: Boolean(available), updatedAt: new Date().toISOString(), updatedBy: "admin-panel" },
    { merge: true }
  );
  if (cache?.[id]) cache[id] = { ...cache[id], available: Boolean(available) };
}

/**
 * Writes one state/subject status.
 *
 * Note what this does NOT do: it never touches a user account. Switching a
 * subject off closes it to new signups and leaves everyone already using it
 * exactly where they were. Someone who paid should not lose access over an
 * internal content decision, and a person locked out of an account they are
 * paying for cannot fix it themselves.
 */
export async function saveSubjectStatus(state, subject, status) {
  const id = String(state || "").toLowerCase();
  const current = cache?.[id]?.subjects || {};
  const subjects = { ...current, [subject]: { status } };
  await setDoc(
    doc(db, "stateSettings", id),
    { subjects, updatedAt: new Date().toISOString(), updatedBy: "admin-panel" },
    { merge: true }
  );
  if (cache?.[id]) cache[id] = { ...cache[id], subjects };
}

/** Every state's settings as an array, for admin UI. Null if not seeded. */
export function allStateSettings() {
  return cache ? Object.values(cache).sort((a, b) =>
    String(a.stateName).localeCompare(String(b.stateName))) : null;
}

/** Drops the cache so the next read hits Firestore. Call after an admin edit. */
export function refreshStateSettings() {
  cache = null;
  inFlight = null;
}

const settingsFor = (state) =>
  cache ? cache[String(state || "").toLowerCase()] : null;

/**
 * Is this state offered to new signups at all?
 *
 * This governs the signup picker only. It does NOT gate an existing account:
 * someone who already pays for a state keeps working when that state is
 * switched off, because losing access to something you bought over an internal
 * content decision is not a thing the person can fix themselves.
 */
export function isStateAvailable(state) {
  const s = settingsFor(state);
  return s ? Boolean(s.available) : geoAvailable(state);
}

/**
 * "ready" | "coming-soon" | "hidden" for one state and subject.
 *
 * Anything other than "ready" means no content is claimed. "coming-soon" still
 * renders in the picker, greyed — a teacher who looks and finds nothing learns
 * more from "coming soon" than from an absence.
 */
export function subjectStatus(state, subject) {
  const s = settingsFor(state);
  if (s) return s.subjects?.[subject]?.status || "hidden";

  if (subject === "science") return geoScienceReady(state) ? "ready" : "coming-soon";
  if (subject === "math") return geoMathReady(state) ? "ready" : "coming-soon";
  // Social studies has no list of its own in geography.js. It used to be
  // "coming-soon" everywhere because it had only ever been built for
  // California; since the lyrics-to-standards match it is built for every
  // state, so the fallback follows the state's availability like the rest.
  return geoAvailable(state) ? "ready" : "coming-soon";
}

export const isSubjectReady = (state, subject) =>
  subjectStatus(state, subject) === "ready";

/** Subjects with real content for this state, in display order. */
export const readySubjects = (state) =>
  SUBJECTS.filter((s) => isSubjectReady(state, s));

/**
 * Every state offered to new signups, with its per-subject status attached.
 * Shaped for the signup picker so it makes one pass rather than 51 lookups.
 */
export async function availableStatesWithStatus() {
  await loadStateSettings();
  if (!cache) return null; // caller falls back to STATE_GROUPS from geography.js

  return Object.values(cache)
    .filter((s) => s.available)
    .map((s) => ({
      state: s.state,
      stateName: s.stateName,
      ready: SUBJECTS.filter((x) => s.subjects?.[x]?.status === "ready"),
      comingSoon: SUBJECTS.filter((x) => s.subjects?.[x]?.status === "coming-soon"),
    }))
    .sort((a, b) => String(a.stateName).localeCompare(String(b.stateName)));
}
